import './index.scss';
import { useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TableCell, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import { TableFiltersLabel } from './TableFilterLabel';
import { ClearFiltersLabel } from './ClearFiltersLabel';
import { Sort } from '@constants/sort';
import { headCells } from './constants';
import _isEqual from 'lodash/isEqual';
import { AnswerQueryInterface, HeadCell, MgState } from '@types';
import { ArrowDropDownIcon } from '@images/icons';
import { notAppliedAnswersFilters } from '@constants/answers';
import { AnswerSortEnum, FeaturePermission, PagePermission, PermissionActions } from '@enums';
import { setAnswerFiltersPartially } from '@actions/answersPageAction';
import { hasPermissionFeature } from '@hooks/hasPermissionFeature';

const TableHeader: FC = () => {
  const dispatch = useDispatch();
  const filters = useSelector<MgState, AnswerQueryInterface>(
    ({ answersPage }) => answersPage.filters
  );

  const getSortDirection = useCallback(
    (key: string): Sort => {
      const order = filters.order.find((order) => order?.includes(key));
      if (order) {
        return order?.startsWith('-') ? Sort.DESC : Sort.ASC;
      }
      return null;
    },
    [filters]
  );

  const handleSort = (headCell: HeadCell) => {
    if (!Object.values(AnswerSortEnum).find((item) => item?.includes(`${headCell.id}`))) {
      return;
    }
    let value;
    if (!getSortDirection(headCell.id)) {
      value = Object.values(AnswerSortEnum).find((item) => item?.includes(`-${headCell.id}`));
    }
    if (getSortDirection(headCell.id) === Sort.DESC) {
      value = Object.values(AnswerSortEnum).find(
        (item) => item?.includes(`${headCell.id}`) && !item?.startsWith('-')
      );
    }
    dispatch(setAnswerFiltersPartially({ order: [value] }));
  };

  const hasControllers =
    hasPermissionFeature(
      PagePermission.Answers,
      [FeaturePermission.AnswerTable],
      [PermissionActions.Remove]
    ) ||
    hasPermissionFeature(
      PagePermission.Answers,
      [FeaturePermission.AnswerTable],
      [PermissionActions.Update]
    );
  return (
    <TableHead>
      <TableRow className="table-head-row">
        {hasControllers && (
          <TableCell padding="checkbox" className="table-head-cell none-checkbox">
            <Box className="table-head-cell_checkbox"></Box>
          </TableCell>
        )}

        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="justify"
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={getSortDirection(headCell.id)}
            className="table-head-cell"
          >
            <Box className="table-head-cell_content">
              <TableSortLabel
                className="table-sort-label"
                active={Boolean(getSortDirection(headCell.id))}
                direction={getSortDirection(headCell.id) || Sort.DESC}
                onClick={() => handleSort(headCell)}
                component="div"
                hideSortIcon={headCell.hideSortIcon}
                IconComponent={ArrowDropDownIcon}
              >
                {headCell.label}
                {getSortDirection(headCell.id) ? (
                  <span className="visually-hidden">
                    {getSortDirection(headCell.id) === Sort.ASC
                      ? 'sorted ascending'
                      : 'sorted descending'}
                  </span>
                ) : null}
              </TableSortLabel>
              <TableFiltersLabel data={headCell} testId={headCell.testId} />
            </Box>
          </TableCell>
        ))}
        <TableCell padding="checkbox" className="table-clear-label">
          {!_isEqual(filters, notAppliedAnswersFilters) && <ClearFiltersLabel />}
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export { TableHeader };
